import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, MenuItem, Paper, Select, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography, CircularProgress, FormControl, InputLabel,
} from '@mui/material';
import { GoodsReceiptService } from '../../services/goodsReceipt.service';
import { PurchaseOrderService } from '../../services/purchaseOrder.service';
import { ItemService } from '../../services/item.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { SegmentService } from '../../services/segment.service';
import { AuthService } from '../../services/auth.service';
import { PurchaseOrder, PurchaseOrderLine } from '../../interfaces/procurement/purchaseOrder.interface';
import { Item } from '../../interfaces/item.interface';
import { Store } from '../../interfaces/store.interface';
import { Segment } from '../../interfaces/accounting.interface';
import { useToast } from '../../context/ToastContext';
import { canReadSegments } from '../../constants/segmentRoles';

interface LineDraft {
  poLine: PurchaseOrderLine;
  receivedQty: number;
  segmentId?: string;
}

// Quantity still receivable on a line. receivedQty comes from the PO detail read (POSTED receipts
// only -- the same sum the backend's over-receipt guard uses); it is absent on list payloads.
const remainingOf = (l: PurchaseOrderLine) => Math.max(0, l.quantity - (l.receivedQty ?? 0));
const toDrafts = (po: PurchaseOrder): LineDraft[] =>
  po.lines.map(l => ({ poLine: l, receivedQty: remainingOf(l) }));

export const GoodsReceiptCreate: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  // Only roles SegmentController admits can read segments; for the rest the picker is hidden, not 403ing.
  const showSegments = canReadSegments(AuthService.getCurrentUser()?.roles);
  const [searchParams] = useSearchParams();
  const lockedPoId = searchParams.get('purchaseOrderId');

  const [pos, setPos] = useState<PurchaseOrder[]>([]);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState('');
  const [receiptDate, setReceiptDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [linesLoading, setLinesLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const itemMap = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);

  useEffect(() => {
    const init = async () => {
      try {
        const [itemsRes, storesRes] = await Promise.all([
          ItemService.getItemsByInstitution(0, 500),
          getStoresByInstitutionId(0, 500),
        ]);
        setItems(itemsRes.content);
        setStores(storesRes.content);
        // Segments are an optional per-line annotation — never let their fetch block GRN creation.
        if (showSegments) {
          SegmentService.getByInstitution(0, 500)
            .then(res => setSegments((res.content as Segment[]).filter(s => s.active !== false)))
            .catch(() => setSegments([]));
        }

        if (lockedPoId) {
          const po = await PurchaseOrderService.getById(lockedPoId);
          setSelectedPO(po);
          setLines(toDrafts(po));
        } else {
          const [orderedRes, partialRes] = await Promise.all([
            PurchaseOrderService.listByInstitution(0, 500, 'ORDERED'),
            PurchaseOrderService.listByInstitution(0, 500, 'PARTIALLY_RECEIVED'),
          ]);
          setPos([...orderedRes.content, ...partialRes.content]);
        }
      } catch (e) {
        toast.showApiError(e, 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePOChange = async (poId: string) => {
    if (!poId) { setSelectedPO(null); setLines([]); return; }
    // The list endpoint omits nested lines (returns lines: null), so fetch the
    // full PO detail to populate the receivable lines.
    setLinesLoading(true);
    setLines([]);
    try {
      const po = await PurchaseOrderService.getById(poId);
      setSelectedPO(po);
      setLines(toDrafts(po));
    } catch (e) {
      toast.showApiError(e, 'Failed to load purchase order lines');
    } finally {
      setLinesLoading(false);
    }
  };

  const updateReceivedQty = (idx: number, qty: number) => {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, receivedQty: qty } : l));
  };

  const updateSegmentId = (idx: number, segmentId: string) => {
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, segmentId: segmentId || undefined } : l));
  };

  const handleSubmit = async () => {
    if (!selectedPO) { toast.showApiError(null, 'Select a Purchase Order'); return; }
    if (!storeId) { toast.showApiError(null, 'Select a Store'); return; }

    const activeLines = lines.filter(l => l.receivedQty > 0);
    if (activeLines.length === 0) { toast.showApiError(null, 'At least one line must have received quantity > 0'); return; }

    setSaving(true);
    try {
      const dto = {
        purchaseOrderId: selectedPO.id,
        storeId,
        receiptDate,
        notes: notes || undefined,
        institutionId: AuthService.getSelectedInstitution()!!,
        lines: activeLines.map(l => ({
          purchaseOrderLineId: l.poLine.id,
          receivedQty: l.receivedQty,
          ...(l.segmentId ? { segmentId: l.segmentId } : {}),
        })),
      };
      const grn = await GoodsReceiptService.create(dto);
      toast.showSuccess('Goods Receipt created');
      navigate(`/procurement/goods-receipts/${grn.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to create Goods Receipt');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>New Goods Receipt</Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Purchase Order"
            value={selectedPO?.id ?? ''}
            onChange={e => handlePOChange(e.target.value)}
            disabled={!!lockedPoId}
          >
            {lockedPoId && selectedPO ? (
              <MenuItem value={selectedPO.id}>{selectedPO.poNumber}</MenuItem>
            ) : (
              pos.map(po => (
                <MenuItem key={po.id} value={po.id}>{po.poNumber}</MenuItem>
              ))
            )}
          </TextField>

          <TextField
            select
            label="Store"
            value={storeId}
            onChange={e => setStoreId(e.target.value)}
          >
            {stores.map(s => (
              <MenuItem key={s.id} value={s.id!}>{s.storeName}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Receipt Date"
            type="date"
            value={receiptDate}
            onChange={e => setReceiptDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Notes"
            multiline
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
          />
        </Box>
      </Paper>

      {linesLoading && (
        <Paper sx={{ p: 3, mb: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Paper>
      )}

      {!linesLoading && selectedPO && lines.length > 0 && lines.every(l => remainingOf(l.poLine) === 0) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Every line on this purchase order has already been received in full.
        </Alert>
      )}

      {!linesLoading && selectedPO && lines.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Lines</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Line ID</TableCell>
                <TableCell>Item</TableCell>
                <TableCell>Ordered Qty</TableCell>
                <TableCell>Already Received</TableCell>
                <TableCell>Received Qty</TableCell>
                {showSegments && <TableCell>Segment</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, idx) => (
                <TableRow key={l.poLine.id}>
                  <TableCell>{l.poLine.id}</TableCell>
                  <TableCell>{itemMap.get(l.poLine.itemId)?.itemName ?? l.poLine.itemId}</TableCell>
                  <TableCell>{l.poLine.quantity}</TableCell>
                  <TableCell>{l.poLine.receivedQty ?? 0}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={l.receivedQty}
                      onChange={e => updateReceivedQty(idx, Number(e.target.value))}
                      disabled={remainingOf(l.poLine) === 0}
                      inputProps={{ min: 0, max: remainingOf(l.poLine), 'aria-label': `Received quantity for line ${idx + 1}` }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  {showSegments && (
                  <TableCell>
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id={`seg-label-${idx}`}>Segment</InputLabel>
                      <Select
                        labelId={`seg-label-${idx}`}
                        label="Segment"
                        value={l.segmentId ?? ''}
                        onChange={e => updateSegmentId(idx, e.target.value as string)}
                      >
                        <MenuItem value="">(default)</MenuItem>
                        {segments.map(s => (
                          <MenuItem key={s.id} value={s.id!}>{s.code} — {s.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Create GRN'}
        </Button>
        <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default GoodsReceiptCreate;
